import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import styles from './PreferencesPage.module.css';
import { getToken, updateUserPreferences } from '../utils/api';

const STATE = {
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
  NO_AUTH: 'no_auth',
  UNKNOWN_ACTION: 'unknown_action',
};

const SUPPORTED_ACTIONS = ['monthly-digest'];

const actionMeta = {
  'monthly-digest': {
    icon: '📅',
    title: 'Switch to Monthly Digest',
    description:
      'You will receive one concise monthly snapshot instead of weekly check-ins. We\'ll keep your financial runway model active in the background and resurface it monthly.',
  },
};

const PreferencesPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const actionParam = searchParams.get('action') || '';
  const urlToken = searchParams.get('token') || '';

  const [pageState, setPageState] = useState(STATE.LOADING);
  const [message, setMessage] = useState('');

  const applyPreference = useCallback(async (action, token) => {
    const result = await updateUserPreferences({ action, token });
    if (result.ok) {
      setPageState(STATE.SUCCESS);
    } else {
      setMessage(result.message || 'Something went wrong. Please try again.');
      setPageState(STATE.ERROR);
    }
  }, []);

  useEffect(() => {
    if (!SUPPORTED_ACTIONS.includes(actionParam)) {
      setPageState(STATE.UNKNOWN_ACTION);
      return;
    }

    const token = urlToken || getToken();
    if (!token) {
      setPageState(STATE.NO_AUTH);
      return;
    }

    applyPreference(actionParam, token);
  }, [actionParam, urlToken, applyPreference]);

  const meta = actionMeta[actionParam] || {};
  const loginRedirect = `/login?redirect=/preferences?action=${encodeURIComponent(actionParam)}`;

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brandHeader}>
          <span className={styles.brandDot} />
          <span className={styles.brandName}>Richuel</span>
        </div>

        {pageState === STATE.LOADING && (
          <div className={styles.stateWrap}>
            <div className={styles.spinner} aria-label="Loading" />
            <p className={styles.stateText}>Updating your preferences&hellip;</p>
          </div>
        )}

        {pageState === STATE.SUCCESS && (
          <div className={styles.stateWrap}>
            <div className={styles.iconCircle} data-variant="success">
              <span className={styles.iconEmoji}>{meta.icon || '✓'}</span>
            </div>
            <h1 className={styles.title}>You&rsquo;re all set!</h1>
            <p className={styles.body}>
              {meta.description ||
                'Your email preference has been updated successfully.'}
            </p>
            <p className={styles.body}>
              You will now receive a{' '}
              <strong>monthly digest</strong> instead of weekly
              emails. You can change this any time from your account settings.
            </p>
            <div className={styles.actions}>
              <button
                className={styles.primaryBtn}
                onClick={() => navigate('/dashboard')}
                type="button"
              >
                Go to Dashboard
              </button>
              <button
                className={styles.ghostBtn}
                onClick={() => navigate('/login')}
                type="button"
              >
                Sign In
              </button>
            </div>
          </div>
        )}

        {pageState === STATE.ERROR && (
          <div className={styles.stateWrap}>
            <div className={styles.iconCircle} data-variant="error">
              <span className={styles.iconEmoji}>⚠️</span>
            </div>
            <h1 className={styles.title}>Couldn&rsquo;t update preference</h1>
            <p className={styles.body}>{message}</p>
            <p className={styles.body}>
              If this keeps happening, please{' '}
              <a
                href="mailto:support@richuel.com"
                className={styles.link}
              >
                contact support
              </a>{' '}
              and we&rsquo;ll take care of it for you.
            </p>
            <div className={styles.actions}>
              <button
                className={styles.primaryBtn}
                onClick={() => navigate('/login')}
                type="button"
              >
                Sign In
              </button>
            </div>
          </div>
        )}

        {pageState === STATE.NO_AUTH && (
          <div className={styles.stateWrap}>
            <div className={styles.iconCircle} data-variant="info">
              <span className={styles.iconEmoji}>{meta.icon || '📋'}</span>
            </div>
            <h1 className={styles.title}>
              {meta.title || 'Update Preference'}
            </h1>
            <p className={styles.body}>
              {meta.description}
            </p>
            <p className={styles.body}>
              To confirm this change, please sign in first. We&rsquo;ll bring
              you right back here automatically.
            </p>
            <div className={styles.actions}>
              <Link to={loginRedirect} className={styles.primaryBtn}>
                Sign In to Confirm
              </Link>
            </div>
          </div>
        )}

        {pageState === STATE.UNKNOWN_ACTION && (
          <div className={styles.stateWrap}>
            <div className={styles.iconCircle} data-variant="error">
              <span className={styles.iconEmoji}>🔗</span>
            </div>
            <h1 className={styles.title}>Link not recognised</h1>
            <p className={styles.body}>
              This preference link doesn&rsquo;t match any known action. It
              may have expired or been copied incorrectly.
            </p>
            <div className={styles.actions}>
              <button
                className={styles.primaryBtn}
                onClick={() => navigate('/')}
                type="button"
              >
                Go to Home
              </button>
            </div>
          </div>
        )}

        <p className={styles.footer}>
          &copy; {new Date().getFullYear()} Richuel &mdash; Building Your
          Financial Future
        </p>
      </div>
    </div>
  );
};

export default PreferencesPage;
